import { useEffect, useState } from 'react'

const STORAGE_KEY = 'pair2.auth'
const CHANNEL = 'pair2.auth.changed'

export interface User {
  id: string
  phone: string
  name?: string
  signedInAt: number
}

let cache: User | null | undefined = undefined
const listeners = new Set<() => void>()

// Global login-modal control so any component can request sign-in without
// rendering its own modal (avoids stacking-context bugs inside nav, etc).
let modalOpen = false
const modalListeners = new Set<() => void>()
export function openLogin() {
  modalOpen = true
  modalListeners.forEach((l) => l())
}
export function closeLogin() {
  modalOpen = false
  modalListeners.forEach((l) => l())
}
export function useLoginModal() {
  const [, setTick] = useState(0)
  useEffect(() => {
    const h = () => setTick((t) => t + 1)
    modalListeners.add(h)
    return () => {
      modalListeners.delete(h)
    }
  }, [])
  return { isOpen: modalOpen, open: openLogin, close: closeLogin }
}

function load(): User | null {
  if (cache !== undefined) return cache as User | null
  if (typeof window === 'undefined') {
    cache = null
    return null
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    cache = raw ? (JSON.parse(raw) as User) : null
  } catch {
    cache = null
  }
  return cache
}

function save(user: User | null) {
  cache = user
  try {
    if (user) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user))
    else sessionStorage.removeItem(STORAGE_KEY)
  } catch {}
  listeners.forEach((l) => l())
  try {
    window.dispatchEvent(new Event(CHANNEL))
  } catch {}
}

export function getUser(): User | null {
  return load()
}

export function signIn(phone: string, name?: string): User {
  const user: User = {
    id: 'usr_' + Math.random().toString(36).slice(2, 8),
    phone,
    name: name?.trim() || undefined,
    signedInAt: Date.now(),
  }
  save(user)
  return user
}

export function signOut() {
  save(null)
}

export function useAuth() {
  const [, setTick] = useState(0)
  useEffect(() => {
    const handler = () => setTick((t) => t + 1)
    listeners.add(handler)
    window.addEventListener(CHANNEL, handler)
    window.addEventListener('storage', handler)
    return () => {
      listeners.delete(handler)
      window.removeEventListener(CHANNEL, handler)
      window.removeEventListener('storage', handler)
    }
  }, [])
  const user = load()
  return {
    user,
    isSignedIn: !!user,
    signIn,
    signOut,
  }
}
